import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { PlanItem } from '../../../../models/api/reports/policy-filter';
import { ReportsService } from '../../../../services/reports/reports.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { InputComponent } from '../../../core/controls/input.component';
import { PickListComponent } from '../../../core/controls/pick-list.component';
import { FieldContainerComponent } from '../../../core/fields/field-container.component';
import { RadioButtonFieldComponent } from '../../../core/fields/radio-button-field.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { DialogComponent } from '../../../core/panels/dialog.component';
import { FilterSection, ICriteria } from '../edit-filter.component';

@Component({
  selector: 'app-policy-criteria',
  imports: [
    CommonModule,
    SelectFieldComponent,
    RadioButtonFieldComponent,
    PickListComponent,
    InputComponent,
    ButtonComponent,
    DialogComponent,
    FieldContainerComponent,
  ],
  template: `
    <div class="tw-pt-2 tw-w-full tw-min-w-0">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full tw-min-w-0">
        <app-radio-button-field
          [control]="fg.controls.selectMode"
          optionLabel="name"
          optionValue="code"
          [options]="selectModes"
          [showHelpText]="false"
          [vertical]="false"
          label="Selection mode"
          valueClasses="tw-w-full tw-max-w-full"
        />

        <app-select-field
          [control]="fg.controls.plan"
          optionLabel="name"
          optionValue="number"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="plans"
          [showHelpText]="false"
          [loading]="loading"
          label="Plan"
          valueClasses="tw-w-full tw-max-w-full"
        />

        @if (requiresSearch) {
          <app-field-container
            label="Search policies"
            [control]="fg.controls.policySearch"
          >
            <ng-template #value>
              <div
                class="tw-flex tw-flex-wrap tw-gap-2 tw-w-full tw-min-w-0 tw-items-center"
              >
                <div class="tw-flex-1 tw-min-w-0">
                  <app-input
                    screenReaderLabel="Search policies"
                    [control]="fg.controls.policySearch"
                    (keypress)="searchKeyPress($event)"
                  />
                </div>

                <div class="tw-shrink-0">
                  <app-button
                    label="Search"
                    (click)="search()"
                    [disabled]="
                      !fg.controls.policySearch.value ||
                      fg.controls.policySearch.value.length < 3
                    "
                    [loading]="searching"
                  />
                </div>
              </div>
            </ng-template>
          </app-field-container>
        }
      </div>
      <ng-template
        #labeledPickList
        let-label="label"
        let-sourceControl="sourceControl"
        let-targetControl="targetControl"
        let-preserveOrder="preserveOrder"
      >
        <div class="tw-flex tw-flex-col tw-w-full tw-min-w-0">
          <div class="tw-font-bold tw-pr-1">{{ label }}</div>
          <div
            class="policy-plan-picklist-wrapper tw-flex tw-justify-center tw-w-full tw-min-w-0 tw-max-w-full tw-py-4 tw-overflow-x-auto"
          >
            <app-pick-list
              [sourceControl]="sourceControl"
              [targetControl]="targetControl"
              labelName="displayName"
              keyName="id"
              [showMove]="false"
              [preserveOrder]="preserveOrder"
              [showAddRemoveAll]="true"
              (targetChanged)="emitCount()"
              [width]="pickListWidth"
              listClasses="tw-w-full"
            />
          </div>
        </div>
      </ng-template>

      <ng-container
        *ngTemplateOutlet="
          labeledPickList;
          context: {
            label: 'Policy number',
            sourceControl: fg.controls.availablePolicyNumbers,
            targetControl: fg.controls.selectedPolicyNumbers,
            preserveOrder: false,
          }
        "
      ></ng-container>

      <ng-container
        *ngTemplateOutlet="
          labeledPickList;
          context: {
            label: 'Policy period',
            sourceControl: fg.controls.availablePolicyPeriods,
            targetControl: fg.controls.selectedPolicyPeriods,
            preserveOrder: true,
          }
        "
      ></ng-container>
    </div>
    <app-dialog
      [visible]="maxPoliciesDialogVisible"
      [closable]="true"
      label="Search Results Exceeded"
      (visibleChange)="maxPoliciesDialogVisible = $event"
    >
      <ng-template #content>
        <div class="tw-p-4">
          <p>
            There are too many policy numbers to be displayed. Please narrow
            down your search criteria.
          </p>
        </div>
        <div class="tw-flex tw-justify-end tw-p-4">
          <app-button label="OK" (click)="maxPoliciesDialogVisible = false" />
        </div>
      </ng-template>
    </app-dialog>
  `,
  styles: [
    `
      :host ::ng-deep app-field-container > div > div:first-child {
        flex-basis: 100% !important;
      }
      :host ::ng-deep app-field-container > div > div:nth-child(2) {
        flex-basis: 100% !important;
      }

      @media (min-width: 768px) {
        :host ::ng-deep app-field-container > div > div:first-child {
          flex-basis: 130px !important;
          flex-shrink: 0 !important;
          flex-grow: 0 !important;
        }
        :host ::ng-deep app-field-container > div > div:nth-child(2) {
          flex-basis: 0 !important;
          flex-grow: 1 !important;
          min-width: 0 !important;
        }
        :host ::ng-deep app-select,
        :host ::ng-deep app-input {
          display: block;
          min-width: 0;
        }
        :host ::ng-deep .p-select-label > .option-label,
        :host ::ng-deep .p-multiselect-label .option-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    `,
  ],
})
export class PolicyCriteriaComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, ICriteria
{
  private reports = inject(ReportsService);
  private elementRef = inject(ElementRef);

  plans: PlanItem[] = [];
  contractNumber: string;
  requiresSearch = false;
  loading = true;

  @Input({ required: true })
  section: FilterSection;

  selectModes = [
    { name: 'Include', code: 'include' },
    { name: 'Exclude', code: 'exclude' },
  ];

  fg = new FormGroup({
    plan: new FormControl<number[]>([]),
    selectMode: new FormControl<string>('include'),
    policySearch: new FormControl<string>(null, [Validators.minLength(3)]),
    availablePolicyNumbers: new FormControl<
      { id: string; displayName: string }[]
    >([]),
    selectedPolicyNumbers: new FormControl<
      { id: string; displayName: string }[]
    >([]),
    availablePolicyPeriods: new FormControl<
      { id: string; displayName: string }[]
    >([]),
    selectedPolicyPeriods: new FormControl<
      { id: string; displayName: string }[]
    >([]),
  });

  maxPoliciesDialogVisible = false;

  searching = false;

  /** Pick-list width: capped by viewport to prevent horizontal overflow on mobile/tablet */
  pickListWidth = 500;

  constructor() {
    super();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.pickListWidth = this.getPickListWidth();
  }

  private getPickListWidth(): number {
    if (typeof window === 'undefined') return 500;
    const padding = 48;
    const containerEl = this.elementRef?.nativeElement?.parentElement;
    const containerW = containerEl?.offsetWidth ?? 0;
    const viewportMax = Math.min(700, window.innerWidth - padding);
    const containerMax = containerW > 0 ? containerW - 24 : viewportMax;
    const max = Math.min(700, Math.min(viewportMax, containerMax));
    return Math.max(320, max);
  }

  valid(): boolean {
    return true;
  }

  searchKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  setPolicyFromCriteria(criteria: FilterCriteria[]) {
    if (criteria[0]?.operator === 'NOTEQUALTO') {
      this.fg.controls.selectMode.setValue('exclude', { emitEvent: false });
    } else {
      this.fg.controls.selectMode.setValue('include', { emitEvent: false });
    }

    const planNumbers = criteria
      .filter((c) => c.column === 'PLAN_NUMBER')
      .flatMap((c) => c.values.map((v) => Number(v)));
    if (planNumbers.length) {
      this.fg.controls.plan.setValue(planNumbers);
    }

    const policyNumbers = criteria
      .filter((c) => c.column === 'EXCESS_POLICY_NUMBER')
      .flatMap((c) => c.values);
    if (policyNumbers.length) {
      const items = policyNumbers.map((v) => ({ id: v, displayName: v }));
      this.fg.controls.selectedPolicyNumbers.setValue(items);
    }

    const policyPeriodValues = criteria
      .filter((c) => c.column === 'EXCESS_POLICY_PERIOD')
      .flatMap((c) => c.values);
    if (policyPeriodValues.length) {
      const availablePeriods =
        this.fg.controls.availablePolicyPeriods.value || [];
      const matchedPeriods = policyPeriodValues.map((v) => {
        const found = availablePeriods.find((p) => p.id === v);
        return found || { id: v, displayName: v };
      });
      this.fg.controls.selectedPolicyPeriods.setValue(matchedPeriods);
    }

    this.emitCount();
  }

  async ngOnInit() {
    this.section.criteria = this;
    this.pickListWidth = this.getPickListWidth();
    this.setupValueChangeSubscriptions();

    const client = this.reports.selectedClient();
    if (client) {
      this.loadPolicyDetails(client);
    }

    if (this.section.values?.length) {
      this.setPolicyFromCriteria(this.section.values);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.pickListWidth = this.getPickListWidth();
    }, 0);
  }

  @HandleLoadingState()
  private async loadPolicyDetails(contractNumber: string) {
    this.contractNumber = contractNumber;

    this.clearCriteria();
    this.plans = [];
    this.fg.controls.availablePolicyNumbers.setValue([]);
    this.fg.controls.availablePolicyPeriods.setValue([]);

    const details =
      await this.api.filtersCriteria.getPolicyDetails(contractNumber);

    this.plans = details.plans;
    this.requiresSearch = details.maxPoliciesExceeded;

    if (!this.requiresSearch) {
      this.fg.controls.availablePolicyNumbers.setValue(
        details.policyNumbers.map((p) => ({
          id: p.policyNumber,
          displayName: p.policyNumber,
        })),
      );
      this.fg.controls.availablePolicyPeriods.setValue(
        details.policyPeriods.map((p) => ({
          id: p.value,
          displayName: p.period,
        })),
      );
    }
  }

  private setupValueChangeSubscriptions() {
    this.dispose.subs = this.fg.controls.plan.valueChanges.subscribe(() =>
      this.emitCount(),
    );
    this.dispose.subs =
      this.fg.controls.selectedPolicyNumbers.valueChanges.subscribe(() =>
        this.emitCount(),
      );
    this.dispose.subs =
      this.fg.controls.selectedPolicyPeriods.valueChanges.subscribe(() =>
        this.emitCount(),
      );
  }

  emitCount() {
    const totalCount =
      (this.fg.controls.plan.value?.length || 0) +
      (this.fg.controls.selectedPolicyNumbers.value?.length || 0) +
      (this.fg.controls.selectedPolicyPeriods.value?.length || 0);
    this.section.count.set(totalCount);
  }

  @HandleLoadingState({ loadingProperty: 'searching' })
  async search() {
    if (
      !this.fg.controls.policySearch.value ||
      this.fg.controls.policySearch.value.length < 3
    ) {
      return;
    }

    const results = await this.api.filtersCriteria.searchPolicies(
      this.contractNumber,
      this.fg.controls.policySearch.value,
    );

    if (results.maxPoliciesExceeded) {
      this.maxPoliciesDialogVisible = true;
      return;
    }

    this.fg.controls.availablePolicyNumbers.setValue(
      results.policyNumbers.map((p) => ({
        id: p.policyNumber,
        displayName: p.policyNumber,
      })),
    );
    this.fg.controls.availablePolicyPeriods.setValue(
      results.policyPeriods.map((p) => ({
        id: p.value,
        displayName: p.period,
      })),
    );
  }

  getCriteria(): FilterCriteria[] {
    const operator =
      this.fg.controls.selectMode.value === 'include' ? 'EQUALS' : 'NOTEQUALTO';

    const planCriteria = (this.fg.controls.plan.value || []).map(
      (planNum) =>
        ({
          tableAlias: 'claim',
          column: 'PLAN_NUMBER',
          displayName: '',
          operator: operator,
          values: [planNum.toString()],
          lineOfBusiness: '',
          dataType: 'NUMBER',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Policy and plan',
          filterSubsection: '',
        }) as FilterCriteria,
    );

    const policyNumberCriteria = (
      this.fg.controls.selectedPolicyNumbers.value || []
    ).map(
      (item) =>
        ({
          tableAlias: 'excess',
          column: 'EXCESS_POLICY_NUMBER',
          displayName: '',
          operator: operator,
          values: [item.id],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Policy and plan',
          filterSubsection: '',
        }) as FilterCriteria,
    );

    const policyPeriodCriteria = (
      this.fg.controls.selectedPolicyPeriods.value || []
    ).map(
      (item) =>
        ({
          tableAlias: 'excess',
          column: 'EXCESS_POLICY_PERIOD',
          displayName: '',
          operator: operator,
          values: [item.id],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Policy and plan',
          filterSubsection: '',
        }) as FilterCriteria,
    );

    return [...planCriteria, ...policyNumberCriteria, ...policyPeriodCriteria];
  }

  clearCriteria(): void {
    this.fg.controls.plan.setValue([], { emitEvent: false });
    this.fg.controls.selectMode.setValue('include', { emitEvent: false });
    this.fg.controls.selectedPolicyNumbers.setValue([]);
    this.fg.controls.selectedPolicyPeriods.setValue([]);
    this.fg.controls.policySearch.setValue(null, { emitEvent: false });
  }
}
